import { useEffect } from 'react'

import { z } from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { usePublishAttributionsMutation } from '@/pages/operations/pnl-attributions/mutations'

const publishAttributionsSchema = z.object({
  batch_id: z.number().nonoptional(),
  comment: z.string().optional().nullable(),
})

export interface PublishAttributionsDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    fund_name: string
    value_date: string
    batch_id: number
  }
}

export const PublishAttributionsDialog: React.FC<PublishAttributionsDialogProps> = ({ mutationParams, ...props }) => {
  const publishAttributionsMutation = usePublishAttributionsMutation({
    fund_name: mutationParams.fund_name,
    value_date: mutationParams.value_date,
  })

  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: { batch_id: mutationParams.batch_id },
    },
    publishAttributionsSchema,
  )

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await publishAttributionsMutation.mutateAsync(data)
    props.onClose?.()
  })

  return (
    <ConfirmationDialog
      {...props}
      action={() => onSubmit()}
      title={`Publish attributions for ${mutationParams.fund_name} on ${mutationParams.value_date}`}
      description={'This will publish the attributions, make sure you have reviewed the data before proceeding.'}
      pending={publishAttributionsMutation.isPending}
    >
      <Form {...form}>
        <form>
          <FormFieldRow>
            <FormField
              name="comment"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-1">
                      <FormLabel>Comment</FormLabel>
                      <FormError />
                    </div>
                    <Textarea value={field.value ?? ''} onChange={field.onChange} />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
        </form>
      </Form>
    </ConfirmationDialog>
  )
}
